import React, { useEffect, useState, useRef } from "react";
import { useUser } from "../../utensils/UserContext";
import { useConfig } from "../../utensils/ConfigContext";
import useAdminTemplates from "../../market/warehouse-baserow/useAdminTemplates";
import useAdminReservation from "../../market/warehouse-baserow/useAdminReservation";
import SnackNewTemplate from "./SnackNewTemplate";
import SnackNewReservation from "./SnackNewReservation";
import SnackAttendanceModal from "./SnackAttendanceModal";
import { FaTrash, FaEdit, FaCalendarPlus } from "react-icons/fa";
import { Tabs, Tab } from "react-bootstrap";
import "./SnackMyClass.css";
import useAdminClasses from "../../market/warehouse-baserow/useAdminClasses";

const SnackMyTemplate = ({ shouldRefresh, onClassCreated }) => {
  const { user } = useUser();
  const { apiToken } = useConfig();
  const {
    templates,
    loading,
    error,
    refetch,
    deleteTemplate, // ✅ asegurarse de que el hook exporte deleteTemplate correctamente
  } = useAdminTemplates();
  const { getReservationsByClassId, deleteReservation } = useAdminReservation();

  useEffect(() => {
    refetch(); // ✅ Cargar plantillas al montar el componente
  }, [refetch]);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [selectedClassId, setSelectedClassId] = useState(null);
  const [zonaActiva, setZonaActiva] = useState("ZONA VIP METAGYM");
  const [reservasPorHorario, setReservasPorHorario] = useState({});
  const [attendanceModalOpen, setAttendanceModalOpen] = useState(false);
  const [selectedClassSchedule, setSelectedClassSchedule] = useState({
    classId: null,
    day: null,
  });
  const { refetch: refetchClases, deleteClass, classes } = useAdminClasses();

  const isUserRole = user?.Rol === "2-usuario";
  const intervalRef = useRef(null);
  const isInClassTab = window.location.pathname.includes("reservas");

  const fetchReservas = async () => {
    try {
      const res = await fetch(
        `https://api.baserow.io/api/database/rows/table/569475/?user_field_names=true&size=200`,
        {
          headers: { Authorization: `Token ${apiToken}` },
        }
      );
      const data = await res.json();
      const agrupadas = {};
      data.results.forEach((r) => {
        const clave = `${r.Clase}||${r.Reserva}`;
        agrupadas[clave] = (agrupadas[clave] || 0) + 1;
      });
      setReservasPorHorario(agrupadas);
    } catch (err) {
      console.error("Error cargando reservas:", err.message);
    }
  };

  useEffect(() => {
    if (apiToken && isInClassTab) fetchReservas();
  }, [apiToken, isInClassTab]);

  useEffect(() => {
    if (!shouldRefresh || !apiToken) return;

    const updatePeriodically = () => {
      if (document.visibilityState === "visible") {
        fetchReservas();
      }
    };

    intervalRef.current = setInterval(updatePeriodically, 10000);
    document.addEventListener("visibilitychange", updatePeriodically);

    return () => {
      clearInterval(intervalRef.current);
      document.removeEventListener("visibilitychange", updatePeriodically);
    };
  }, [apiToken, shouldRefresh]);
  const openNewClass = () => {
    setEditingId(null);
    setModalOpen(true);
  };

  const openEditClass = (id) => {
    setEditingId(id);
    setModalOpen(true);
  };

  const openReservation = (classId) => {
    setSelectedClassId(classId);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setEditingId(null);
    setSelectedClassId(null);
    refetch();
    fetchReservas();
  };

  const handleDelete = async (classId) => {
    const confirm = window.confirm(
      "¿Estás seguro de eliminar esta plantilla y todas sus reservas?"
    );
    if (!confirm) return;

    try {
      const reservas = await getReservationsByClassId(classId);
      const emails = [...new Set(reservas.map((r) => r.Email).filter(Boolean))];
      const claseNombre =
        templates.find((c) => c.id === classId)?.Nombre ||
        "Plantilla eliminada";

      if (emails.length > 0) {
        await fetch("https://fisiofitnessapp.com/mail/deleted_class.php", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ clase: claseNombre, emails }),
        });
      }

      for (const reserva of reservas) {
        try {
          await deleteReservation(reserva.id);
        } catch (err) {
          console.warn(
            `No se pudo eliminar reserva ID ${reserva.id}:`,
            err.message
          );
        }
      }

      await deleteTemplate(classId); // ✅ Usar deleteTemplate del hook actualizado

      refetch();
      fetchReservas();
    } catch (err) {
      console.error("Error eliminando plantilla o reservas:", err.message);
      alert(
        "Ocurrió un error al intentar eliminar la plantilla o sus reservas."
      );
    }
  };

  const openAttendanceList = (classId, day) => {
    setSelectedClassSchedule({ classId, day });
    setAttendanceModalOpen(true);
  };
  // Pon este helper arriba (o fuera del componente)
  const minutesDiff = (inicio, fin) => {
    if (!inicio || !fin) return 0;
    const [sh, sm] = String(inicio).split(":").map(Number);
    const [eh, em] = String(fin).split(":").map(Number);
    const diff = eh * 60 + em - (sh * 60 + sm);
    return diff > 0 ? diff : 0;
  };

  // Reemplaza COMPLETAMENTE tu formatSchedule por esta versión
  const formatSchedule = (jsonString, cupo, classId) => {
    try {
      const obj =
        typeof jsonString === "string" ? JSON.parse(jsonString) : jsonString;

      const orderedDays = [
        "Lunes",
        "Martes",
        "Miércoles",
        "Jueves",
        "Viernes",
        "Sábado",
        "Domingo",
      ];

      return orderedDays.map((day) => {
        const dayObj = obj?.[day] || { titulo: "", franjas: [] };
        const titulo = dayObj.titulo || "";
        const franjas = Array.isArray(dayObj.franjas) ? dayObj.franjas : [];

        const totalDia = franjas.reduce(
          (acc, f) => acc + minutesDiff(f.inicio, f.fin),
          0
        );

        return (
          <div key={`${classId}-${day}`} className="mb-2">
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <span className="badge bg-light text-dark">{day}</span>
              {titulo ? (
                <span className="badge bg-info text-dark">{titulo}</span>
              ) : null}
              {totalDia > 0 ? (
                <small className="text-muted ms-auto">
                  Total día: {totalDia} min
                </small>
              ) : null}
            </div>

            {franjas.length > 0 ? (
              <div className="small text-muted mt-1">
                {franjas.map((f, idx) => {
                  const mins = minutesDiff(f.inicio, f.fin);
                  return (
                    <div key={`${classId}-${day}-${idx}`} className="ms-2">
                      • {f.inicio} - {f.fin} {mins ? `(${mins} min)` : ""}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="small text-muted ms-2">-</div>
            )}
          </div>
        );
      });
    } catch (error) {
      console.error("Error al parsear Programacion:", error);
      return <span className="text-danger">Horario no válido</span>;
    }
  };

  if (loading) return <p>Cargando plantillas...</p>;
  if (error) return <p>Error: {error}</p>;

  const handleCloneToClass = async (template) => {
    try {
      const nuevaClase = {
        Nombre: template.Nombre,
        Detalle: template.Detalle,
        Instructor: template.Instructor,
        Programacion: template.Programacion,
        Estado: true, // activa por defecto
        Cupo: template.Cupo,
        Zona: template.Zona,
      };

      const res = await fetch(
        "https://api.baserow.io/api/database/rows/table/569481/?user_field_names=true",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Token ${apiToken}`,
          },
          body: JSON.stringify(nuevaClase),
        }
      );

      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(`Error al crear clase: ${res.status} - ${errorText}`);
      }

      const data = await res.json();
      alert(`✅ Clase creada a partir de la plantilla: ${data.Nombre}`);
      refetchClases();

      if (onClassCreated) {
        onClassCreated(); // 🔁 Notificar al padre (SnackProfile) que debe actualizar "Mis clases"
      }
    } catch (err) {
      console.error("🚨 Error al crear clase desde plantilla:", err.message);
      alert("Ocurrió un error al crear la clase desde la plantilla.");
    }
  };

  return (
    <div className="training-plan-container">
      <div className="d-flex justify-content-between align-items-center p-3 bg-white rounded shadow-sm mb-4 border">
        <h2 className="m-0">Plantillas de clases</h2>
        {!isUserRole && (
          <button className="btn btn-success" onClick={openNewClass}>
            Nueva plantilla
          </button>
        )}
      </div>
      <Tabs
        activeKey={zonaActiva}
        onSelect={(k) => setZonaActiva(k)}
        className="mb-3"
      >
        {["ZONA VIP METAGYM", "ZONA VIP FITNESS FOR LIFE"].map((zona) => (
          <Tab eventKey={zona} title={zona} key={zona}>
            <div className="workouts-cards-container">
              {Array.isArray(templates) &&
                templates
                  .filter((tpl) => tpl.Zona === zona)
                  .map((tpl) => (
                    <div key={tpl.id} className="card shadow-sm p-3 mb-3">
                      <h5 className="text-primary">
                        {tpl.Nombre} - ({tpl.Cupo} Cupos)
                      </h5>
                      <p>
                        <strong>Instructor:</strong> {tpl.Instructor}
                      </p>
                      <p>
                        <strong>Detalle:</strong> {tpl.Detalle}
                      </p>
                      <p>
                        <strong>Horario:</strong>
                        <div className="ms-2 schedule-scroll">
                          {formatSchedule(tpl.Programacion, tpl.Cupo, tpl.id)}
                        </div>
                      </p>
                      <p>
                        <strong>Zona:</strong> {tpl.Zona}
                      </p>
                      <div className="mt-3 d-flex justify-content-between">
                        {isUserRole ? (
                          <button
                            className="btn btn-outline-success btn-sm w-100"
                            onClick={() => openReservation(tpl.id)}
                          >
                            <FaCalendarPlus className="me-1" /> Reservar
                          </button>
                        ) : (
                          <>
                            <button
                              className="btn btn-outline-primary btn-sm me-2"
                              onClick={() => openEditClass(tpl.id)}
                            >
                              <FaEdit className="me-1" /> Editar
                            </button>

                            <button
                              className="btn btn-outline-warning btn-sm me-2"
                              onClick={() => handleCloneToClass(tpl)}
                            >
                              <FaCalendarPlus className="me-1" /> Crear una
                              clase
                            </button>

                            <button
                              className="btn btn-outline-danger btn-sm"
                              onClick={() => handleDelete(tpl.id)}
                            >
                              <FaTrash className="me-1" /> Eliminar
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
            </div>
          </Tab>
        ))}
      </Tabs>

      {!isUserRole ? (
        <SnackNewTemplate
          show={modalOpen}
          onHide={handleModalClose}
          classId={editingId}
          onSave={handleModalClose}
        />
      ) : (
        <SnackNewReservation
          show={modalOpen}
          onHide={handleModalClose}
          classId={selectedClassId}
          onSave={handleModalClose}
        />
      )}

      <SnackAttendanceModal
        show={attendanceModalOpen}
        onHide={() => setAttendanceModalOpen(false)}
        classId={selectedClassSchedule.classId}
        day={selectedClassSchedule.day}
      />
    </div>
  );
};

export default SnackMyTemplate;
